import { useCallback, useEffect, useRef, useState } from "react";
import { SHIFTIQ_API, getAuthHeaders, isAbortError } from "../api/client";
import { normalizeSessionRole } from "../identity/access";
import { allowedStoresForUser, storeMatchKey } from "../lib/storeScope";

const POLL_MS = 2 * 60 * 1000;
export const PROCURE_ORDERS_BADGE_REFRESH = "procure-orders-badge-refresh";

export function requestProcureOrdersBadgeRefresh() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PROCURE_ORDERS_BADGE_REFRESH));
}

function isStoreManagerRole(user) {
  const role = normalizeSessionRole(user?.role);
  return role === "store_manager" || role === "manager";
}

function locationMatchesAllowed(loc, allowedNames) {
  const locKey = storeMatchKey(loc?.location_name);
  if (!locKey) return false;
  return (allowedNames || []).some((name) => storeMatchKey(name) === locKey);
}

/**
 * Open-due receipt count for T1 managers' stores (nav red badge).
 * Admins / brand managers get 0 here — overview is email, not a nav nag.
 */
export function useProcureOrdersBadge(user, { refreshKey } = {}) {
  const [count, setCount] = useState(0);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (!user || !isStoreManagerRole(user)) {
      setCount(0);
      return;
    }
    const allowed = allowedStoresForUser(user);
    if (!Array.isArray(allowed) || !allowed.length) {
      setCount(0);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const locRes = await fetch(`${SHIFTIQ_API}/api/procureiq/active-locations`, {
        headers: getAuthHeaders(),
        signal: controller.signal,
      });
      if (!locRes.ok) {
        setCount(0);
        return;
      }
      const locData = await locRes.json().catch(() => ({}));
      const locations = Array.isArray(locData?.locations) ? locData.locations : [];
      const mine = locations.filter((loc) => locationMatchesAllowed(loc, allowed));
      if (!mine.length) {
        setCount(0);
        return;
      }

      let total = 0;
      await Promise.all(
        mine.map(async (loc) => {
          const id = Number(loc.unified_location_id);
          if (!Number.isFinite(id)) return;
          const brand = Number(loc.brand_id) || 15;
          const res = await fetch(
            `${SHIFTIQ_API}/api/procureiq/receipt-review?location_id=${id}&brand_id=${brand}`,
            { headers: getAuthHeaders(), signal: controller.signal }
          );
          if (!res.ok) return;
          const data = await res.json().catch(() => ({}));
          total += Number(data.open_due_count) || 0;
        })
      );
      if (!controller.signal.aborted) setCount(total);
    } catch (err) {
      if (isAbortError(err)) return;
      setCount(0);
    }
  }, [user]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, POLL_MS);
    const onRefresh = () => load();
    window.addEventListener(PROCURE_ORDERS_BADGE_REFRESH, onRefresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(PROCURE_ORDERS_BADGE_REFRESH, onRefresh);
      abortRef.current?.abort();
    };
  }, [load, refreshKey]);

  return count;
}
